import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { HD_BODYGRAPH_GATES, HD_BODYGRAPH_GATES_BY_CENTER, HD_BODYGRAPH_GATE_TO_CENTER } from '../../hd-bodygraph-metadata';
import { HdBodygraphCenter } from '../../hd-bodygraph.models';
import { HD_CENTER_SHAPE_SVG } from '../../hd-center-shapes';
import { HdGateInfoContext } from '../hd-gate-info-context.model';
import { HdGateInfoCheatSheetComponent } from '../variants/hd-gate-info-cheat-sheet.component';
import { HD_BODYGRAPH_WARM_DUSK_SUBTLE_COLOR_CONFIG, HdBodygraphColorConfig } from '../../hd-bodygraph-color-config';
import {
  HD_CARD_FEEDBACK_DURATION_MS,
  HdCardFeedbackComponent,
  HdCardFeedbackKind,
} from '../../card-feedback/hd-card-feedback.component';
import { HdGameFeedbackAudioService } from '../../hd-game-feedback-audio.service';

/**
 * Toont een set Human Design poorten als cheat-sheet kaarten in een responsief grid.
 *
 * Standaard alle 64 poorten in canonieke volgorde — de cheat-sheet-pagina kan
 * via `[gates]` een geschudde volgorde meegeven (dice-knop). Bedoeld voor:
 * - de cheat-sheet-pagina (volledig overzicht voor studie/print);
 * - de hd-experiment-page (visuele validatie tijdens iteratie).
 *
 * De grid beheert per-kaart reveal-state: wanneer minstens één hide-flag actief
 * is en de speler op een kaart klikt, toont die specifieke kaart alle info
 * (per-card override). De set blijft bewaard over toggles, filters en de
 * spelmodus heen (Steven 2026-10-29: heen en weer wisselen zonder verlies).
 *
 * **Inkleur-spelmodus** (`paintMode`): de kleuren staan verborgen, de speler
 * kiest in de controls-bar een center als "verfemmer" (`paintCenter`) en klikt
 * kaarten aan. Hoort de poort bij dat center, dan wordt de kaart onthuld (zelfde
 * reveal-set als een gewone klik, dus ze blijft open na de spelmodus) met de
 * goed-animatie; anders blijft ze dicht en toont de fout-animatie.
 */
@Component({
  selector: 'hd-cheat-sheet-grid',
  templateUrl: './hd-cheat-sheet-grid.component.html',
  styleUrl: './hd-cheat-sheet-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdGateInfoCheatSheetComponent, HdCardFeedbackComponent],
})
export class HdCheatSheetGridComponent {
  /** Volgorde van te tonen poorten. Default = canonieke 1..64 reeks. */
  readonly gates = input<readonly number[]>(HD_BODYGRAPH_GATES);

  readonly hideGateNumber = input(false);
  readonly hideKeynotes = input(false);
  readonly hideColors = input(false);
  readonly hideFooterCircuit = input(false);
  readonly hideFooterChannel = input(false);
  readonly hideBody = input(false);
  readonly bodyView = input<'default' | 'lines'>('default');
  readonly colorConfig = input<HdBodygraphColorConfig | null>(null);

  /** Inkleur-spelmodus aan: klik = inkleurpoging in plaats van reveal-toggle. */
  readonly paintMode = input(false);
  /** Gekozen center (verfemmer) in de inkleur-spelmodus; `null` = nog geen keuze. */
  readonly paintCenter = input<HdBodygraphCenter | null>(null);

  private readonly feedbackAudio = inject(HdGameFeedbackAudioService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _revealedGates = signal<ReadonlySet<number>>(new Set());
  /** Onthulde poorten (klik of juiste inkleurpoging); de pagina leest dit voor de validatie. */
  readonly revealedGates = this._revealedGates.asReadonly();

  /** Lopende goed/fout-animaties per poort. */
  private readonly _feedback = signal<ReadonlyMap<number, HdCardFeedbackKind>>(new Map());
  private readonly feedbackTimers = new Map<number, ReturnType<typeof setTimeout>>();

  /**
   * Cursor voor de kaarten in de inkleurmodus: het FA-verfemmer-icoon in de
   * donkere paletkleur met linksboven het mini-centericoon van het gekozen
   * center in zijn paletkleur (de kleur die je giet).
   * Eerst meteen een SVG-terugval, daarna (zodra het FA-font getekend kan
   * worden) het echte glyph via een canvas.
   */
  private readonly _paintCursor = signal<string | null>(null);
  protected readonly paintCursor = this._paintCursor.asReadonly();

  private readonly paintColors = computed<HdPaintCursorColors | null>(() => {
    const center = this.paintCenter();
    if (!this.paintMode() || center == null) {
      return null;
    }
    const colors = this.colorConfig() ?? HD_BODYGRAPH_WARM_DUSK_SUBTLE_COLOR_CONFIG;
    // De donkere kanaalkleur (zwart/dusk) van het actieve palet als icoonkleur.
    return { icon: colors.channelPersonality, center, centerColor: colors.centerDefined[center] };
  });

  /** Elke inkleurpoging (ook een genegeerde); de pagina wist er haar validatie-uitkomst mee. */
  readonly paintAttempted = output<void>();

  constructor() {
    let cursorRequest = 0;
    effect(() => {
      const colors = this.paintColors();
      const request = ++cursorRequest;
      untracked(() => {
        if (colors == null) {
          this._paintCursor.set(null);
          return;
        }
        this._paintCursor.set(buildHdPaintBucketCursor(colors));
        void renderHdPaintBucketCursor(colors).then(cursor => {
          if (cursor != null && request === cursorRequest) {
            this._paintCursor.set(cursor);
          }
        });
      });
    });

    this.destroyRef.onDestroy(() => this.clearAllFeedback());
  }

  protected contextFor(gate: number): HdGateInfoContext {
    return { gate };
  }

  protected isRevealed(gate: number): boolean {
    return this._revealedGates().has(gate);
  }

  protected feedbackFor(gate: number): HdCardFeedbackKind | null {
    return this._feedback().get(gate) ?? null;
  }

  protected onCardClicked(gate: number): void {
    if (this.paintMode()) {
      this.paintAttempted.emit();
      this.tryPaint(gate);
      return;
    }
    const next = new Set(this._revealedGates());
    if (next.has(gate)) {
      next.delete(gate);
    } else {
      next.add(gate);
    }
    this._revealedGates.set(next);
  }

  /**
   * Inkleurpoging: zonder gekozen center gebeurt er niets; een al onthulde
   * kaart ook niet. Juist center = kaart onthuld + goed-animatie, fout center
   * = enkel de fout-animatie.
   */
  private tryPaint(gate: number): void {
    const center = this.paintCenter();
    if (center == null || this._revealedGates().has(gate)) {
      return;
    }
    if (HD_BODYGRAPH_GATE_TO_CENTER[gate] === center) {
      this._revealedGates.set(new Set([...this._revealedGates(), gate]));
      this.showFeedback(gate, 'correct');
      this.feedbackAudio.playCorrect();
      return;
    }
    this.showFeedback(gate, 'wrong');
    this.feedbackAudio.playWrong();
  }

  private showFeedback(gate: number, kind: HdCardFeedbackKind): void {
    const running = this.feedbackTimers.get(gate);
    if (running != null) {
      clearTimeout(running);
    }
    this.setFeedback(gate, kind);
    this.feedbackTimers.set(gate, setTimeout(() => {
      this.feedbackTimers.delete(gate);
      this.setFeedback(gate, null);
    }, HD_CARD_FEEDBACK_DURATION_MS));
  }

  private setFeedback(gate: number, kind: HdCardFeedbackKind | null): void {
    const next = new Map(this._feedback());
    if (kind == null) {
      next.delete(gate);
    } else {
      next.set(gate, kind);
    }
    this._feedback.set(next);
  }

  private clearAllFeedback(): void {
    for (const timer of this.feedbackTimers.values()) {
      clearTimeout(timer);
    }
    this.feedbackTimers.clear();
    if (this._feedback().size > 0) {
      this._feedback.set(new Map());
    }
  }

  protected trackGate(_index: number, gate: number): number {
    return gate;
  }
}

/** Uitkomst van de validatie in de inkleur-spelmodus: één center, of alles (`center: null`). */
export interface HdCheatSheetPaintValidation {
  center: HdBodygraphCenter | null;
  /** Poorten die nog niet onthuld zijn. */
  missing: number;
  total: number;
}

/**
 * Telt hoeveel poorten van het center (of van alle 64 bij `null`) nog niet in
 * de onthulde set zitten.
 */
export function validateHdCheatSheetPaintCenter(
  center: HdBodygraphCenter | null,
  revealed: ReadonlySet<number>,
): HdCheatSheetPaintValidation {
  const gates = center == null ? HD_BODYGRAPH_GATES : HD_BODYGRAPH_GATES_BY_CENTER[center] ?? [];
  return {
    center,
    missing: gates.filter(gate => !revealed.has(gate)).length,
    total: gates.length,
  };
}

/** Opbouw van de verfemmer-cursor: het icoon zelf (donker) en het mini-centericoon linksboven. */
export interface HdPaintCursorColors {
  icon: string;
  center: HdBodygraphCenter;
  centerColor: string;
}

/** FontAwesome Pro 7 solid, codepoint van `fill-drip` (zie vendor/fontawesome-pro/scss/_variables.scss). */
const HD_FA_FILL_DRIP_GLYPH = '';
const HD_FA_SOLID_FONT = '900 20px "Font Awesome 7 Pro"';
const HD_PAINT_CURSOR_SIZE = 36;
/**
 * Middelpunt van het (gespiegelde) icoon: naar rechtsonder, zodat het
 * centericoon linksboven (de kleur die je giet) vrij staat.
 */
const HD_PAINT_CURSOR_ICON_CENTER = { x: 22, y: 22 };
/** Vak van het mini-centericoon linksboven (de vorm schaalt erin, verhoudingen blijven). */
const HD_PAINT_CURSOR_CENTER_BOX = { x: 1, y: 1, size: 12 };
/**
 * Hotspot op de punt van de druppel. Het FA-glyph heeft de druppel rechtsonder;
 * gespiegeld (hengsel rechts) komt hij linksonder. Opgemeten op het canvas:
 * druppel x 11 tot 15, onderkant y 33.
 */
const HD_PAINT_CURSOR_HOTSPOT = '13 32';

/**
 * Tekent het FA-verfemmer-icoon (`fill-drip`, hetzelfde icoon als op de
 * spelmodus-knop) in de donkere paletkleur op een canvas, met linksboven het
 * mini-centericoon in de centerkleur, en geeft de CSS-`cursor` terug. Het icoon wordt horizontaal
 * gespiegeld: zo zit het hengsel rechts, zoals een rechtshandige de emmer
 * vasthoudt (Steven 2026-10-29), en komt de druppel linksonder. `null`
 * wanneer het font (nog) niet beschikbaar is of canvas ontbreekt (tests, oude
 * browsers); de host houdt dan de SVG-terugval.
 */
export async function renderHdPaintBucketCursor(colors: HdPaintCursorColors): Promise<string | null> {
  if (typeof document === 'undefined' || !('fonts' in document)) {
    return null;
  }
  try {
    await document.fonts.load(HD_FA_SOLID_FONT, HD_FA_FILL_DRIP_GLYPH);
    if (!document.fonts.check(HD_FA_SOLID_FONT, HD_FA_FILL_DRIP_GLYPH)) {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = HD_PAINT_CURSOR_SIZE;
    canvas.height = HD_PAINT_CURSOR_SIZE;
    const context = canvas.getContext('2d');
    if (context == null) {
      return null;
    }
    context.font = HD_FA_SOLID_FONT;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = colors.icon;
    // Spiegelen rond het icoon-middelpunt.
    context.save();
    context.translate(HD_PAINT_CURSOR_ICON_CENTER.x, HD_PAINT_CURSOR_ICON_CENTER.y);
    context.scale(-1, 1);
    context.fillText(HD_FA_FILL_DRIP_GLYPH, 0, 0);
    context.restore();
    await drawHdPaintCursorCenter(context, colors);
    return `url("${canvas.toDataURL('image/png')}") ${HD_PAINT_CURSOR_HOTSPOT}, cell`;
  } catch {
    return null;
  }
}

/**
 * Tekent het mini-centericoon linksboven op het canvas: de centervorm wordt als
 * SVG-afbeelding geladen (zelfde markup als de SVG-terugval) en erop gelegd.
 */
async function drawHdPaintCursorCenter(context: CanvasRenderingContext2D, colors: HdPaintCursorColors): Promise<void> {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${HD_PAINT_CURSOR_SIZE}" height="${HD_PAINT_CURSOR_SIZE}">`
    + buildHdPaintCursorCenterMarkup(colors)
    + `</svg>`;
  const image = new Image();
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await image.decode();
  context.drawImage(image, 0, 0);
}

/**
 * Het mini-centericoon (dezelfde vorm als de center-mini-kaartjes) als geneste
 * SVG in het vak linksboven: vulling in de centerkleur, met een wit randje
 * zodat het op elke kaartkleur leesbaar blijft.
 */
export function buildHdPaintCursorCenterMarkup(colors: HdPaintCursorColors): string {
  const box = HD_PAINT_CURSOR_CENTER_BOX;
  const shapeAttributes =
    `fill="${colors.centerColor}" stroke="#ffffff" stroke-width="1.5" vector-effect="non-scaling-stroke"`;
  return HD_CENTER_SHAPE_SVG[colors.center]
    .replace(/<svg width="[^"]*" height="[^"]*"/,
      `<svg x="${box.x}" y="${box.y}" width="${box.size}" height="${box.size}" preserveAspectRatio="xMidYMid meet" overflow="visible"`)
    .replace('<rect ', `<rect ${shapeAttributes} `)
    .replace('<path ', `<path ${shapeAttributes} `);
}

/**
 * Directe SVG-terugval voor de cursor (eenvoudige verfemmer in de donkere
 * kleur, gespiegeld zoals het canvas-icoon, + mini-centericoon linksboven), tot
 * het FA-glyph getekend is. Zelfde maat en hotspot als de canvas-variant.
 */
export function buildHdPaintBucketCursor(colors: HdPaintCursorColors): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">`
    // emmer + hengsel + druppel zoals het gespiegelde canvas-icoon: hengsel rechts,
    // druppel linksonder (punt op de hotspot), vrij van het centericoon linksboven
    + `<g transform="translate(2 0)">`
    + `<path d="M19 9.5 31 21.5 22.5 30 10.5 18z" fill="${colors.icon}"/>`
    + `<path d="M20.5 11.5c2-3.5 6.5-3.5 8 .5" fill="none" stroke="${colors.icon}" stroke-width="2" stroke-linecap="round"/>`
    + `<path d="M11 21c-1.2 2.8-3 4.8-3 7.6 0 2 1.3 3.4 3 3.4s3-1.4 3-3.4c0-2.8-1.8-4.8-3-7.6z" fill="${colors.icon}"/>`
    + `</g>`
    // mini-centericoon
    + buildHdPaintCursorCenterMarkup(colors)
    + `</svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}") ${HD_PAINT_CURSOR_HOTSPOT}, cell`;
}
